/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { ChangeDetectionStrategy, Component, input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * Shared page header used by every reference screen (UI-01 … UI-20):
 * a rounded icon tile, the page title, a supporting line, and an optional
 * action area projected into the right-hand side.
 *
 * ```html
 * <aca-page-header icon="cloud_upload" title="อัปโหลดเอกสาร" subtitle="…">
 *   <button mat-flat-button>…</button>
 * </aca-page-header>
 * ```
 */
@Component({
  selector: 'aca-page-header',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'aca-page-header', '[class.aca-page-header--bare]': 'bare()' }
})
export class PageHeaderComponent {
  /** Material icon ligature shown in the tile. */
  icon = input<string>('');
  /** Page title. */
  title = input<string>('');
  /** Supporting line under the title. */
  subtitle = input<string>('');
  /**
   * Drops the tile, the title and the supporting line, leaving only the
   * projected actions. Used where a screen is shown inside another that
   * already names it — the settings page, for one — so the actions stay put
   * without every such screen having to repeat them.
   */
  bare = input<boolean>(false);
}
